import React, { useMemo } from 'react';
import { Colors, Text } from '@mercator-gcp/grout';
import Dropdown from 'components/elements/Dropdown';
import DropdownItem from 'components/elements/Dropdown/DropdownItem';
import { Spinner } from 'components/elements/Spinner';
import ProjectStageOptions from 'components/widgets/FilterToolbar/filters/LatestProjectStage/ProjectStageOptions';
import { useFilterToolbarErrors } from 'hooks/useFilterToolbar';
import { useCurrentRegion } from 'state/currentRegion';
import { useDashboardTriggers, useUserStateSelection } from 'state/dashboard';
import {
  useEventTriggerCategoriesByState,
  useEventTriggerDocTypeByStateCode,
  useEventTriggerStatusesByState,
} from 'state/eventTriggers';
import { DataMap, useLatestProjectStageFilterState } from 'state/filterToolbar';
import { projectStageCategoriesByState } from 'utils/constants/projectStageCategoriesByState';
import { cityMap } from 'utils/misc';

const panelBox = {
  justifyContent: 'space-between',
  display: 'flex',
  mb: 'padding_8',
  flexWrap: 'wrap',
  width: '100%',
};

const descriptionText = {
  color: Colors.TextQuiet,
};

const LatestProjectStage: React.FC = () => {
  const { currentRegion } = useCurrentRegion();
  const [currentTriggers, saveTriggers] = useDashboardTriggers();
  const [selectedState] = useUserStateSelection();
  const {
    latestProjectStageFilterState: selectedOptions,
    setLatestProjectStageFilterState: setSelectedOptions,
    resetLatestProjectStageFilterState,
  } = useLatestProjectStageFilterState();

  const cityDocumentTypes = useEventTriggerDocTypeByStateCode(selectedState);
  const { latestStageFilterError } = useFilterToolbarErrors();
  const { data: allCategories, isLoading: categoriesLoading } =
    useEventTriggerCategoriesByState(
      selectedState,
      cityDocumentTypes.map((doc) => doc.id),
    );
  const { data: allStatuses, isLoading: statusesLoading } =
    useEventTriggerStatusesByState(
      selectedState,
      cityDocumentTypes.map((doc) => doc.id),
    );

  const projectStageOptions = useMemo(() => {
    if (!currentRegion || !allCategories?.length || !allStatuses?.length) {
      return {};
    }

    const statusCategories = projectStageCategoriesByState[String(selectedState)];

    return cityDocumentTypes.reduce(
      (accumulator, docType) => {
        const categories =
          docType.categories?.reduce(
            (childAccumulator, category) => {
              childAccumulator[category] = {
                label: allCategories.find((cat) => cat.id === category)?.name ?? '',
                value: category,
              };

              return childAccumulator;
            },
            {} as Record<number, any>,
          ) ?? {};

        const statuses =
          docType.statuses?.reduce(
            (childAccumulator, status) => {
              const label =
                allStatuses.find((stat) => stat.id === status && stat)?.name ?? '';

              const cleanedDocTypeName = docType.name.replace(/\s+/g, '-').toLowerCase();
              const categoriesForStatus = statusCategories
                ? statusCategories[cleanedDocTypeName]
                : undefined;
              const key = categoriesForStatus?.[label] || 'Other';

              if (key === undefined || typeof key !== 'string') {
                return childAccumulator;
              }

              if (childAccumulator[key] === undefined) {
                childAccumulator[key] = {
                  label: key,
                  value: key,
                  children: {
                    [status]: {
                      label,
                      value: status,
                    },
                  },
                };
              } else {
                childAccumulator[key].children[status] = {
                  label,
                  value: status,
                };
              }

              return childAccumulator;
            },
            {} as Record<string, any>,
          ) ?? {};

        accumulator[docType.id] = {
          label: docType.name,
          value: docType.id,
          children: {
            [`categories-${docType.id}`]: {
              label: 'Categories',
              value: `categories-${docType.id}`,
              children: categories,
            },
            [`statuses-${docType.id}`]: {
              label: 'Statuses',
              value: `statuses-${docType.id}`,
              children: statuses,
            },
          },
        };

        return accumulator;
      },
      {} as Record<number, any>,
    );
  }, [cityDocumentTypes, allCategories, allStatuses]);

  const filterCount = useMemo(() => {
    const totalCount =
      (currentTriggers?.length && currentTriggers[0]?.documentTypes?.length) || 0;

    const totalOptions = Object.keys(projectStageOptions).length;

    // If checkedState is not present, return an empty string
    if (totalCount === 0 || !totalCount) {
      return '';
    }

    // If totalCount is not equal to totalOptions and is greater than 0, return totalCount
    if (totalCount !== totalOptions && totalCount > 0) {
      return totalCount.toString();
    }

    return 'All';
  }, [currentTriggers, projectStageOptions]);

  return (
    <Dropdown
      title="Latest Project Stage"
      filterCount={filterCount}
      errorMessage={latestStageFilterError}
    >
      <DropdownItem sx={panelBox}>
        <Text.Small sx={descriptionText}>
          A project’s current stage according to it’s latest activity
        </Text.Small>
      </DropdownItem>
      {categoriesLoading || statusesLoading ? (
        <Spinner />
      ) : (
        <ProjectStageOptions
          dashboardTriggers={currentTriggers}
          setSelectedOptions={setSelectedOptions}
          selectedOptions={selectedOptions}
          resetSelectedOptions={resetLatestProjectStageFilterState}
          isLoading={categoriesLoading || statusesLoading}
          options={projectStageOptions as DataMap}
          onChange={(newValues) => {
            saveTriggers([
              {
                cityId: cityMap.AUSTIN,
                documentTypes: newValues,
              },
            ]);
          }}
        />
      )}
    </Dropdown>
  );
};

export default LatestProjectStage;
